import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Truck, RefreshCcw, ShieldCheck, Gem, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, money, errMsg, METAL_SWATCH } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { Stars, Spinner, FinanceCue, inputCls, Markdown } from "@/components/common";
import { InstalmentCue } from "@/components/Instalments";
import { ProductCard } from "@/components/ProductCard";
import { NotFound } from "@/pages/Content";
import { ShareButton } from "@/components/ShareButton";
import { ShareLinks, ShareCountBadge } from "@/components/ShareLinks";
import { ReviewUploads, ReviewMediaView } from "@/components/ReviewMedia";
import { RingSizeHelp } from "@/components/RingSizeHelp";
import { EditionInfo } from "@/components/EditionInfo";

const SIZES = ["4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10"];
const SPEC_LABELS: Record<string, string> = { stone_shape: "Centre shape", stone_size: "Centre carat", total_carat_weight: "Total carat weight", color: "Colour", clarity: "Clarity",
  main_stones: "Main stone", secondary_stones: "Accent stones", prong_types: "Prongs", band_width: "Band width", weight: "Metal weight", certifications: "Certification" };

const toneOf = (m: string) => (/yellow/i.test(m) ? "yellow" : /rose/i.test(m) ? "rose" : "white");

export function imagesForMetal(p: any, metal: string): string[] {
  const tones: Record<string, string> = {};
  (p.image_tones || []).forEach((t: any) => { tones[t.url] = t.tone; });
  (p.cms?.image_tone_overrides || []).forEach((t: any) => { tones[t.url] = t.tone; });
  const want = toneOf(metal || "");
  const matched = p.images.filter((u: string) => tones[u] === want);
  const shared = p.images.filter((u: string) => tones[u] === "all");
  return matched.length ? [...matched, ...shared] : p.images;
}

function Gallery({ images, name }: any) {
  const [i, setI] = useState(0);
  return (
    <div className="lg:sticky lg:top-28 self-start min-w-0" data-testid="product-gallery">
      <div className="bg-white aspect-square overflow-hidden"><img src={images[i]} alt={name} className="w-full h-full object-contain p-6 md:p-10" data-testid="product-main-image" /></div>
      <div className="flex gap-3 mt-3 overflow-x-auto">
        {images.map((src: string, n: number) => (
          <button key={src} onClick={() => setI(n)} className={`h-20 w-20 flex-none bg-white border ${n === i ? "border-neutral-900" : "border-transparent hover:border-neutral-300"}`} data-testid={`gallery-thumb-${n}`}>
            <img src={src} alt="" className="w-full h-full object-contain p-1" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewForm({ productId, onDone }: any) {
  const [f, setF] = useState<any>({ rating: 5, title: "", body: "" });
  const [photos, setPhotos] = useState<string[]>([]);
  const [video, setVideo] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { const r = await api.post(`/products/${productId}/reviews`, { ...f, photos, video }); toast(r.data.message); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 bg-white p-6 border border-neutral-200" data-testid="review-form">
      <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} data-testid={`review-rating-${n}`} className={n <= f.rating ? "text-[var(--c-primary)]" : "text-neutral-300"}>★</button>)}</div>
      <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="review-title-input" />
      <textarea required placeholder="Share your experience" rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={inputCls} data-testid="review-body-input" />
      <ReviewUploads photos={photos} setPhotos={setPhotos} video={video} setVideo={setVideo} />
      <p className="text-xs text-neutral-600" data-testid="review-vip-note">Earn <Link to="/pages/rewards" className="underline">Somnio VIP</Link> rewards once your review is approved — more when you add a photo or video.</p>
      <button className="btn-ink px-8 py-3 text-xs tracking-widest uppercase" data-testid="review-submit-button">Submit Review</button>
    </form>
  );
}

export default function Product() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { user, wishlist, toggleWish } = useAuth();
  const [d, setD] = useState<any>(null);
  const [missing, setMissing] = useState(false);
  const [metal, setMetal] = useState("");
  const [size, setSize] = useState("");
  const [engraving, setEngraving] = useState("");
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    setD(null); setMissing(false);
    api.get(`/products/${slug}`).then((r) => { setD(r.data); setMetal(r.data.product.primary_metal || r.data.product.metals?.[0] || ""); }).catch(() => setMissing(true));
  }, [slug]);

  const p = d?.product;
  useMeta(p ? p.cms?.seo_title || `${p.name} | Somnio.Co` : undefined, p ? p.cms?.seo_description || p.description?.slice(0, 155) : undefined,
    p ? { "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.sku, image: p.images, description: p.description,
      offers: { "@type": "Offer", priceCurrency: "AUD", price: p.price, availability: "https://schema.org/InStock" },
      ...(p.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } } : {}) } : null,
    { image: p?.images?.[0], noindex: !!p?.cms?.noindex, type: "product" });

  if (missing) return <NotFound />;
  if (!p) return <Spinner />;
  const isRing = p.product_type === "Rings";
  const price = d.product.metal_prices?.[metal] ?? p.price;
  const isSetting = isRing && (p.style === "Engagement" || p.tags?.includes("Engagement Rings"));
  const saved = wishlist.includes(p.id);
  const gallery = imagesForMetal(p, metal);

  const addToBag = () => {
    if (isRing && !size) { toast.error("Please select a ring size"); return; }
    add({ product_id: p.id, name: p.name, slug: p.slug, image: gallery[0] || p.image, metal, ring_size: size, engraving, unit_price: price });
  };
  const specs = Object.entries(p.specs || {}).filter(([k, v]: any) => SPEC_LABELS[k] && v && (!Array.isArray(v) || v.length) && v !== "—");

  return (
    <div data-testid="product-page">
      <nav className="max-w-[1400px] mx-auto px-5 md:px-12 pt-6 text-xs text-neutral-500" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-neutral-900">Home</Link> / <Link to={`/collections/${isRing ? "engagement-rings" : "fine-jewellery"}`} className="hover:text-neutral-900">{p.product_type}</Link> / <span>{p.name}</span>
      </nav>
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-8 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-20">
        <Gallery key={metal} images={gallery} name={p.name} />
        <div>
          <p className="eyebrow text-[var(--c-primary)]">{p.style || p.product_type}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight mt-3" data-testid="product-title">{p.name}</h1>
          <div className="flex items-center gap-3 mt-4 text-xs text-neutral-500">
            <Stars value={p.rating} /> <a href="#reviews" className="underline">{p.review_count ? `${p.review_count} review${p.review_count === 1 ? "" : "s"}` : "Be the first to review"}</a> <span>SKU {p.sku}</span>
          </div>
          <p className="text-2xl mt-6" data-testid="product-price">{money(price)}</p>
          <div className="mt-2"><InstalmentCue price={price} testId="product-instalment-cue" /><FinanceCue price={price} /></div>
          <EditionInfo p={p} />

          {p.metals?.length > 0 && (
            <div className="mt-9">
              <p className="eyebrow mb-3">Metal: <span className="text-neutral-500 normal-case tracking-normal">{metal}</span></p>
              <div className="flex gap-3 flex-wrap">
                {p.metals.map((m: string) => (
                  <button key={m} onClick={() => setMetal(m)} title={m} data-testid={`metal-option-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`h-11 w-11 rounded-full border-2 grid place-items-center transition-colors ${metal === m ? "border-neutral-900" : "border-transparent hover:border-neutral-300"}`}>
                    <span className="h-8 w-8 rounded-full border border-neutral-300" style={{ background: METAL_SWATCH[m] || "#ddd" }} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {isRing && (
            <div className="mt-7">
              <div className="flex justify-between items-center mb-3"><p className="eyebrow">Ring size</p><RingSizeHelp sizes={SIZES} value={size} onPick={setSize} /></div>
              <div className="grid grid-cols-7 gap-2">
                {SIZES.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`ring-size-${s}`} className={`py-2.5 text-sm border transition-colors ${size === s ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {isRing && (
            <div className="mt-7">
              <p className="eyebrow mb-3">Complimentary engraving <span className="text-neutral-400 normal-case tracking-normal">(optional)</span></p>
              <input maxLength={30} value={engraving} onChange={(e) => setEngraving(e.target.value)} placeholder="Up to 30 characters" className={inputCls} data-testid="engraving-input" />
            </div>
          )}
          <div className="mt-9 flex gap-3">
            <button onClick={addToBag} className="btn-ink flex-1 py-4 text-xs tracking-[0.25em] uppercase" data-testid="add-to-bag-button">Add to Bag</button>
            <button onClick={async () => { if (!user) { nav("/login"); return; } await toggleWish(p.id); toast(saved ? "Removed from wishlist" : "Saved to wishlist"); }}
              className="btn-line w-14 grid place-items-center" aria-label="Wishlist" data-testid="pdp-wishlist-button">
              <Heart size={18} strokeWidth={1.3} className={saved ? "fill-[var(--c-primary)] text-[var(--c-primary)]" : ""} />
            </button>
            <ShareButton path={`/products/${p.slug}`} title={p.name} className="btn-line w-14 justify-center" />
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-5" data-testid="pdp-share-row">
            <span className="eyebrow text-neutral-500">Share</span>
            <ShareLinks path={`/products/${p.slug}`} title={p.name} image={p.images?.[0] || ""} track={`/products/${p.slug}/share`} testPrefix="pdp-share" className="flex items-center gap-5" />
            <ShareCountBadge slug={p.slug} />
          </div>
          {isSetting && (
            <Link to={`/ring-builder?setting=${p.id}`} className="btn-line block text-center mt-3 py-4 text-xs tracking-[0.25em] uppercase" data-testid="customize-stone-button">Customise Centre Stone</Link>
          )}
          <div className="grid grid-cols-2 gap-4 mt-9 text-xs text-neutral-600">
            <span className="flex items-center gap-2"><Truck size={16} strokeWidth={1.2} /> Free insured shipping</span>
            <span className="flex items-center gap-2"><RefreshCcw size={16} strokeWidth={1.2} /> 180-day damage cover</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.2} /> Lifetime warranty</span>
            <span className="flex items-center gap-2"><Gem size={16} strokeWidth={1.2} /> Conflict-free lab diamonds</span>
          </div>
          <Link to="/consultation" className="mt-6 flex items-center gap-3 bg-[var(--c-muted)] p-4 text-sm hover:bg-neutral-200 transition-colors" data-testid="pdp-consultation-link">
            <CalendarDays size={18} strokeWidth={1.2} /> Questions? Book a free virtual or showroom consultation
          </Link>
          <Accordion type="multiple" defaultValue={["desc"]} className="mt-9">
            <AccordionItem value="desc"><AccordionTrigger className="eyebrow">Description</AccordionTrigger><AccordionContent className="text-sm leading-relaxed text-neutral-600">{p.description}{p.cms?.extra_content && <div className="mt-4"><Markdown text={p.cms.extra_content} /></div>}</AccordionContent></AccordionItem>
            {specs.length > 0 && (
              <AccordionItem value="specs"><AccordionTrigger className="eyebrow">Specifications</AccordionTrigger><AccordionContent>
                <dl className="grid grid-cols-2 gap-y-3 text-sm" data-testid="product-specs">
                  {specs.map(([k, v]: any) => <React.Fragment key={k}><dt className="text-neutral-500">{SPEC_LABELS[k]}</dt><dd>{Array.isArray(v) ? v.join(", ") : String(v)}</dd></React.Fragment>)}
                </dl>
              </AccordionContent></AccordionItem>
            )}
            <AccordionItem value="ship"><AccordionTrigger className="eyebrow">Shipping & Returns</AccordionTrigger><AccordionContent className="text-sm text-neutral-600">Handcrafted to order and dispatched within 3–4 weeks with complimentary insured shipping. Accidental damage covered for 180 days. <Link to="/pages/shipping-returns" className="underline">Details</Link></AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      <section id="reviews" className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 border-t border-neutral-200 mt-10" data-testid="reviews-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div><h2 className="font-display text-3xl md:text-4xl font-light">Client Reviews</h2>{p.review_count > 0 && <p className="mt-2 text-sm flex items-center gap-2"><Stars value={p.rating} /> {p.rating} · {p.review_count} review{p.review_count === 1 ? "" : "s"}</p>}</div>
          <button onClick={() => (user ? setWriting(!writing) : nav("/login"))} className="btn-line px-7 py-3 text-xs tracking-widest uppercase" data-testid="write-review-button">Write a Review</button>
        </div>
        {writing && <div className="mb-10 max-w-xl"><ReviewForm productId={p.id} onDone={() => setWriting(false)} /></div>}
        {d.reviews.length === 0 ? <p className="text-sm text-neutral-500" data-testid="no-reviews">No reviews yet.</p> : (
          <div className="grid md:grid-cols-2 gap-6">
            {d.reviews.map((r: any) => (
              <div key={r.id} className="border border-neutral-200 bg-white p-6" data-testid="review-item">
                <Stars value={r.rating} /><p className="font-display text-xl mt-3">{r.title}</p><p className="text-sm text-neutral-600 mt-2 leading-relaxed">{r.body}</p>
                <ReviewMediaView r={r} />
                <p className="eyebrow text-[10px] text-neutral-500 mt-4">{r.author}{r.verified_buyer ? " · Verified buyer" : ""}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {d.related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-10" data-testid="related-products">
          <h2 className="font-display text-3xl font-light mb-10">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 md:gap-x-8">{d.related.slice(0, 4).map((r: any, i: number) => <ProductCard key={r.id} p={r} index={i} />)}</div>
        </section>
      )}
    </div>
  );
}
